// src/pages/ContactsPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Checkbox,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    Alert,
    Snackbar,
    Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import ContactEditor from '../components/Contacts/ContactEditor';
import EngagementDialog from '../components/Contacts/EngagementDialog';
import api from '../api/axios';

const ContactsPage = () => {
    const [contacts, setContacts] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedContact, setSelectedContact] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [showCampaignDialog, setShowCampaignDialog] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [editingContact, setEditingContact] = useState(null);
    const [engagementFilter, setEngagementFilter] = useState('all');
    const [engagementDialogOpen, setEngagementDialogOpen] = useState(false);
    const [selectedContactForEngagement, setSelectedContactForEngagement] = useState(null);

    const debouncedSearch = useDebounce(searchQuery, 500);

    // Consolidated fetch functions
    const fetchContacts = async (pageNum = 0, search = '') => {
        setLoading(true);
        try {
            const response = await api.get('/user-contacts/', {
                params: {
                    page: pageNum + 1,
                    page_size: rowsPerPage,
                    search: search,
                    engagement_status: (engagementFilter !== 'all' && engagementFilter !== 'bounced') ? engagementFilter : undefined,
                    bounced: engagementFilter === 'bounced' ? 1 : undefined,
                }
            });
            setContacts(response.data.results);
            setTotalCount(response.data.count);
        } catch (error) {
            console.error('Error fetching contacts:', error);
            setSnackbar({
                open: true,
                message: 'Error loading contacts',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchCampaigns = async () => {
        try {
            console.log('Fetching campaigns...'); // Debug log
            // Make sure we're using the correct endpoint
            const response = await api.get('/campaigns/', {
                params: {
                    status: 'active' // Add status filter if your API supports it
                }
            });
            console.log('Campaigns response:', response.data); // Debug log
            
            if (Array.isArray(response.data)) {
                const activeCampaigns = response.data.filter(campaign => 
                    campaign.status !== 'completed' && 
                    campaign.status !== 'cancelled'
                );
                console.log('Filtered active campaigns:', activeCampaigns);
                setCampaigns(activeCampaigns);
            } else if (response.data.results) {
                // Handle paginated response
                const activeCampaigns = response.data.results.filter(campaign => 
                    campaign.status !== 'completed' && 
                    campaign.status !== 'cancelled'
                );
                console.log('Filtered active campaigns from paginated response:', activeCampaigns);
                setCampaigns(activeCampaigns);
            }
        } catch (error) {
            console.error('Error fetching campaigns:', error.response || error);
            setSnackbar({
                open: true,
                message: `Error loading campaigns: ${error.response?.data?.detail || error.message}`,
                severity: 'error'
            });
        }
    };

    const getEngagementStatusDetails = (status) => {
        const statusConfig = {
            'none': { label: 'No Engagement', color: 'default' },
            'engaged': { label: 'In Discussion', color: 'primary' },
            'meeting_scheduled': { label: 'Meeting Scheduled', color: 'success' },
            'customer': { label: 'Customer', color: 'secondary' },
            'do_not_email': { label: 'Do Not Email', color: 'error' }
        };
        return statusConfig[status] || { label: 'Unknown', color: 'default' };
    };

    const getEngagementLabel = (status) => {
        const labels = {
            'none': 'No Engagement',
            'engaged': 'In Discussion',
            'meeting_scheduled': 'Meeting Scheduled',
            'customer': 'Customer',
            'do_not_email': 'Do Not Email'
        };
        return labels[status] || status;
    };
    
    const getEngagementColor = (status) => {
        const colors = {
            'none': 'default',
            'engaged': 'primary',
            'meeting_scheduled': 'success',
            'customer': 'secondary',
            'do_not_email': 'error'
        };
        return colors[status] || 'default';
    };

    // UseEffects
    useEffect(() => {
        fetchContacts(page, debouncedSearch);
    }, [page, rowsPerPage, debouncedSearch, engagementFilter]);

    useEffect(() => {
        fetchCampaigns();
    }, []);

    useEffect(() => {
        if (showCampaignDialog) {
            fetchCampaigns();
        }
    }, [showCampaignDialog]);

    // Consolidated handlers
    const handleDeleteContact = async (contactId) => {
        try {
            await api.delete(`/user-contacts/${contactId}/`);
            setSnackbar({
                open: true,
                message: 'Contact deleted successfully',
                severity: 'success'
            });
            fetchContacts(page, debouncedSearch);
            setDeleteDialogOpen(false);
            setSelectedContact(null);
        } catch (error) {
            console.error('Error deleting contact:', error);
            setSnackbar({
                open: true,
                message: 'Error deleting contact',
                severity: 'error'
            });
        }
    };

    const handleAddToDoNotEmail = async (contact) => {
        try {
            await api.post('/do-not-email/', {
                value: contact.email,
                reason: 'Added from contacts page'
            });
            setSnackbar({
                open: true,
                message: 'Added to Do Not Email list',
                severity: 'success'
            });
            fetchContacts(page, debouncedSearch);
        } catch (error) {
            console.error('Error adding to do-not-email list:', error);
            setSnackbar({
                open: true,
                message: 'Error adding to Do Not Email list',
                severity: 'error'
            });
        }
    };

    const handleEditContact = (contact) => {
        setEditingContact(contact);
        setEditDialogOpen(true);
    };

    const handleEditSave = async (updatedData) => {
        try {
            await api.patch(`/user-contacts/${editingContact.id}/`, updatedData);
            setSnackbar({
                open: true,
                message: 'Contact updated successfully',
                severity: 'success'
            });
            fetchContacts(page, debouncedSearch);
            setEditDialogOpen(false);
            setEditingContact(null);
        } catch (error) {
            console.error('Error updating contact:', error.response || error);
            
            // Handle validation errors
            if (error.response?.data) {
                const errorMessages = [];
                Object.entries(error.response.data).forEach(([field, errors]) => {
                    // Handle both array and string error messages
                    if (Array.isArray(errors)) {
                        // Extract just the error message from ErrorDetail objects
                        const messages = errors.map(err => 
                            typeof err === 'object' && err.string ? err.string : err
                        );
                        errorMessages.push(`${field.replace('_', ' ')}: ${messages.join(', ')}`);
                    } else if (typeof errors === 'string') {
                        errorMessages.push(`${field.replace('_', ' ')}: ${errors}`);
                    }
                });
    
                // Format the error message
                const formattedMessage = 'Please correct the following:\n' + 
                    errorMessages.map(msg => '• ' + msg.charAt(0).toUpperCase() + msg.slice(1)).join('\n');
    
                setSnackbar({
                    open: true,
                    message: formattedMessage,
                    severity: 'error'
                });
            } else {
                setSnackbar({
                    open: true,
                    message: 'Error updating contact: ' + (error.message || 'Unknown error'),
                    severity: 'error'
                });
            }
        }
    };

    const handleEngagementUpdate = (contact) => {
        setSelectedContactForEngagement(contact);
        setEngagementDialogOpen(true);
    };

    const handleEngagementSave = async (engagementData) => {
        try {
            console.log('Saving engagement data:', engagementData); // Debug log
            const response = await api.patch(
                `/user-contacts/${selectedContactForEngagement.id}/`, 
                engagementData
            );
            console.log('Save response:', response.data); // Debug log
            
            setSnackbar({
                open: true,
                message: 'Engagement status updated successfully',
                severity: 'success'
            });
            
            // Refresh the contacts list
            await fetchContacts(page, debouncedSearch);
            
            setEngagementDialogOpen(false);
            setSelectedContactForEngagement(null);
        } catch (error) {
            console.error('Error updating engagement status:', error);
            setSnackbar({
                open: true,
                message: 'Error updating engagement status: ' + 
                    (error.response?.data?.detail || error.message),
                severity: 'error'
            });
        }
    };

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setPage(0);
    };

    // Selection handlers
    const handleSelectAll = (event) => {
        setSelectAll(event.target.checked);
        setSelectedContacts(event.target.checked ? contacts.map(contact => contact.id) : []);
    };

    const handleSelectContact = (contactId) => {
        setSelectedContacts(prev => 
            prev.includes(contactId) 
                ? prev.filter(id => id !== contactId)
                : [...prev, contactId]
        );
    };

    const handleAddToCampaign = async () => {
        if (!selectedCampaign) {
            setSnackbar({
                open: true,
                message: 'Please select a campaign',
                severity: 'warning'
            });
            return;
        }

        try {
            const response = await api.post(`/campaigns/${selectedCampaign}/add_contacts/`, {
                contact_ids: selectedContacts
            });
            setSnackbar({
                open: true,
                message: response.data.message || 'Contacts added to campaign successfully',
                severity: 'success'
            });
            setShowCampaignDialog(false);
            setSelectedContacts([]);
            setSelectAll(false);
            setSelectedCampaign('');
            fetchContacts(page, debouncedSearch); // Refresh to update campaign chips
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Error adding contacts to campaign',
                severity: 'error'
            });
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Contacts</Typography>
                <Button 
                    component={Link} 
                    to="/contacts/do-not-email"
                    color="primary"
                    startIcon={<BlockIcon />}
                >
                    Do Not Email List
                </Button>
            </Box>

            {/* Replace the single TextField with this Box containing both controls */}
            <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                mb: 3,
                alignItems: 'flex-start'  // Aligns items at the top
            }}>
                <TextField
                    sx={{ flex: 1 }}  // Takes up remaining space
                    label="Search Contacts"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search by name, email, or company..."
                />
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Engagement Status</InputLabel>
                    <Select
                        value={engagementFilter}
                        onChange={(e) => setEngagementFilter(e.target.value)}
                        label="Engagement Status"
                    >
                        <MenuItem value="all">All Statuses</MenuItem>
                        <MenuItem value="none">No Engagement</MenuItem>
                        <MenuItem value="engaged">In Discussion</MenuItem>
                        <MenuItem value="meeting_scheduled">Meeting Scheduled</MenuItem>
                        <MenuItem value="customer">Customer</MenuItem>
                        <MenuItem value="do_not_email">Do Not Email</MenuItem>
                        <MenuItem value="bounced">Bounced (needs email fix)</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {selectedContacts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowCampaignDialog(true)}
                    >
                        Add {selectedContacts.length} Contact{selectedContacts.length !== 1 ? 's' : ''} to Campaign
                    </Button>
                </Box>
            )}

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    indeterminate={selectedContacts.length > 0 && selectedContacts.length < contacts.length}
                                />
                            </TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Company</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Campaigns</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contacts.map((contact) => (
                            <TableRow key={contact.id}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedContacts.includes(contact.id)}
                                        onChange={() => handleSelectContact(contact.id)}
                                    />
                                </TableCell>
                                <TableCell>{`${contact.first_name} ${contact.last_name}`}</TableCell>
                                <TableCell>
                                    {contact.email}
                                    {contact.bounced_at && (
                                        <Tooltip title={contact.bounce_reason || 'Hard bounce'}>
                                            <Chip
                                                label="Bounced"
                                                color="error"
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell>{contact.company_name}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={getEngagementStatusDetails(contact.engagement_status).label}
                                        color={getEngagementStatusDetails(contact.engagement_status).color}
                                        size="small"
                                        onClick={() => handleEngagementUpdate(contact)}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    {contact.campaigns?.map(campaign => (
                                        <Chip
                                            key={campaign.id}
                                            label={campaign.name}
                                            size="small"
                                            color={campaign.status === 'active' ? 'primary' : 'default'}
                                            sx={{ mr: 1, mb: 1 }}
                                            onClick={() => 
                                                window.open(`/campaigns/${campaign.id}`, '_blank')
                                            }
                                        />
                                    ))}
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleEditContact(contact)}
                                        title="Edit Contact"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteContact(contact.id)}
                                        title="Delete Contact"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleAddToDoNotEmail(contact)}
                                        title="Add to Do Not Email"
                                    >
                                        <BlockIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </TableContainer>

            {/* Campaign Selection Dialog */}
            <Dialog 
                open={showCampaignDialog} 
                onClose={() => {
                    setShowCampaignDialog(false);
                    setSelectedCampaign('');
                }}
            >
                <DialogTitle>
                    Add {selectedContacts.length} Contact{selectedContacts.length !== 1 ? 's' : ''} to Campaign
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Campaign</InputLabel>
                        <Select
                            value={selectedCampaign}
                            onChange={(e) => setSelectedCampaign(e.target.value)}
                            label="Campaign"
                        >
                            {campaigns.length === 0 ? (
                                <MenuItem disabled value="">
                                    No active campaigns available
                                </MenuItem>
                            ) : (
                                campaigns.map((campaign) => (
                                    <MenuItem key={campaign.id} value={campaign.id}>
                                        {campaign.name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                    {campaigns.length === 0 && (
                        <Typography color="error" sx={{ mt: 2 }}>
                            No active campaigns available. Please create a new campaign first.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setShowCampaignDialog(false);
                        setSelectedCampaign('');
                    }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleAddToCampaign}
                        variant="contained"
                        disabled={!selectedCampaign || campaigns.length === 0}
                    >
                        Add to Campaign
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Contact</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this contact? 
                    This will remove them from all campaigns.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteContact} 
                        color="error"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <ContactEditor
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setEditingContact(null);
                }}
                contact={editingContact}
                onSave={handleEditSave}
            />

            <EngagementDialog
                    open={engagementDialogOpen}
                    onClose={() => {
                        setEngagementDialogOpen(false);
                        setSelectedContactForEngagement(null);
                    }}
                    contact={selectedContactForEngagement}
                    onSave={handleEngagementSave}
            />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    sx={{ 
                        whiteSpace: 'pre-line',
                        '& .MuiAlert-message': { 
                            maxHeight: '300px', 
                            overflowY: 'auto',
                            '& ul': { 
                                margin: 0,
                                paddingLeft: 2
                            }
                        }
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Box>
    );
};

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

export default ContactsPage;